/**
 * Road segments and their district-heating viability.
 *
 * The four classes mirror backend `_classify_tramo`, and the thresholds are
 * repeated in the labels on purpose: the colour alone says which class a street
 * is in, never why, and "why" is the whole question a viability map is asked.
 *
 *   ambos → density ≥ 1,5 MWh/m·año AND LCOE geo < LCOE aero (4G and 5G both)
 *   4g    → density ≥ 1,5 MWh/m·año, aero wins on LCOE
 *   5g    → density below the threshold, geo wins → 5G ambient loop
 *   aero  → individual aerothermal beats both
 */

export const VIABILIDADES = ["ambos", "4g", "5g", "aero", "sin_datos"] as const;

export type Viabilidad = (typeof VIABILIDADES)[number];

export const VIAB_COLORS: Record<Viabilidad, string> = {
  ambos: "#7B3FBF", // violet: both options on the table
  "4g": "#1863DC",
  "5g": "#5EA69B",
  aero: "#D8613C",
  sin_datos: "#9e9e9e",
};

export const VIAB_LABELS: Record<Viabilidad, string> = {
  ambos: "Red 4G + 5G — ambas viables",
  "4g": "4G — red térmica tradicional (≥1.5 MWh/m·año)",
  "5g": "5G — anillo ambiente con bombas de calor",
  aero: "Aerotermia individual mejor (LCOE)",
  sin_datos: "Sin análisis",
};

/** Short labels, for the legend where the threshold is already stated once. */
export const VIAB_SHORT: Record<Viabilidad, string> = {
  ambos: "Ambos · 4G + 5G viables",
  "4g": "4G · densidad ≥ 1,5 MWh/m·año",
  "5g": "5G · geo < aero (LCOE)",
  aero: "Aerotermia individual",
  sin_datos: "Sin análisis",
};

export function isViabilidad(value: unknown): value is Viabilidad {
  return typeof value === "string"
    && (VIABILIDADES as readonly string[]).includes(value);
}

/** The class of a segment, falling back to the no-data class. */
export function viabilidadOf(value: unknown): Viabilidad {
  return isViabilidad(value) ? value : "sin_datos";
}

/**
 * kWh → MWh. The endpoint reports every energy figure in kWh, which runs to six
 * digits for a single street and stops being readable at a glance.
 */
export function mwh(value: number | null | undefined, decimals = 1): string {
  if (value == null || !Number.isFinite(value)) return "–";
  return (value / 1000).toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** LCOE arrives in €/kWh and is shown in cents, where the digits are legible. */
export function lcoe(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "–";
  return `${(value * 100).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} c€/kWh`;
}
